import { useEffect, useState } from 'react';
import { FiArrowRight, FiClock, FiSearch, FiInbox } from 'react-icons/fi';
import { Spinner } from '../../components/ui/Spinner';
import { cartaServicosService, type AreaPublica, type AssuntoPublico } from '../../services/cartaServicos.service';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';


/**
 * Página otimizada para embed via <iframe>. Sem header/footer, sem login.
 * Pode ser embutida no site oficial da prefeitura.
 *
 * Uso recomendado:
 *   <iframe
 *     src="https://pedrodetoledo.pontisistemas.com.br/embed/servicos"
 *     style="width:100%;border:0;min-height:600px"
 *     loading="lazy"
 *     title="Carta de Serviços"
 *   ></iframe>
 *
 * Query params:
 *   ?area=<id>     — filtra por área específica
 *   ?busca=<termo> — busca pré-aplicada
 *   ?max=<N>       — máximo de serviços por área (default: todos)
 */
export function ServicosEmbedPage() {
  const params = new URLSearchParams(window.location.search);
  const areaFiltroId = params.get('area');
  const buscaInicial = params.get('busca') ?? '';
  const max = params.get('max') ? parseInt(params.get('max')!, 10) : null;

  const [areas, setAreas] = useState<AreaPublica[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState(buscaInicial);

  useEffect(() => {
    cartaServicosService.listar(getEstabelecimentoAtualId())
      .then((d) => {
        let filtrado = d.filter((a) => a.assuntos.length > 0);
        if (areaFiltroId) filtrado = filtrado.filter((a) => a.id === areaFiltroId);
        setAreas(filtrado);
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
    // Permitir reload se mudar query
  }, [areaFiltroId]);

  const termo = busca.trim().toLowerCase();
  const areasFiltradas = !termo
    ? areas
    : areas
        .map((a) => ({
          ...a,
          assuntos: a.assuntos.filter((s) =>
            s.nome.toLowerCase().includes(termo) ||
            (s.descricao ?? '').toLowerCase().includes(termo),
          ),
        }))
        .filter((a) => a.assuntos.length > 0);

  return (
    <div className="bg-white p-4">
      <div className="mb-4 max-w-md relative">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
        <input
          type="search"
          placeholder="Buscar serviço..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="w-full h-10 pl-9 pr-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional"
        />
      </div>

      {carregando ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : erro ? (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{erro}</div>
      ) : areasFiltradas.length === 0 ? (
        <div className="text-center py-10 text-slate-500">
          <FiInbox className="mx-auto text-slate-300 mb-2" size={32} />
          <p className="text-sm">Nenhum serviço disponível.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {areasFiltradas.map((area) => (
            <section key={area.id ?? 'sem-area'}>
              <h2 className="text-sm font-semibold text-slate-900 mb-2">{area.nome}</h2>
              <ul className="space-y-1">
                {(max ? area.assuntos.slice(0, max) : area.assuntos).map((s) => (
                  <ItemServico key={s.id} servico={s} corArea={area.cor} />
                ))}
              </ul>
              {max && area.assuntos.length > max && (
                <a
                  href={`/servicos${area.id ? `?area=${area.id}` : ''}`}
                  target="_top"
                  className="inline-block mt-2 text-xs text-institutional hover:underline"
                >
                  Ver mais {area.assuntos.length - max} serviço(s)
                </a>
              )}
            </section>
          ))}
          <div className="pt-4 border-t border-slate-100 text-center">
            <a
              href="/servicos"
              target="_top"
              className="text-xs text-institutional hover:underline"
            >
              Ver carta de serviços completa →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function ItemServico({ servico, corArea }: { servico: AssuntoPublico; corArea: string | null }) {
  const cor = corArea ?? '#313b64';
  return (
    <li>
      <a
        href={`/servicos/detalhe/${servico.id}`}
        target="_top"
        className="flex items-center gap-3 p-2.5 rounded border border-slate-200 hover:border-institutional/40 hover:bg-slate-50 transition"
      >
        <div className="flex-shrink-0 w-1 h-8 rounded" style={{ background: cor }} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate">{servico.nome}</div>
          {servico.prazo_dias && (
            <div className="text-xs text-slate-500 flex items-center gap-1">
              <FiClock size={10} /> {servico.prazo_dias} dia(s) útil(eis)
            </div>
          )}
        </div>
        <FiArrowRight className="text-slate-300 flex-shrink-0" size={14} />
      </a>
    </li>
  );
}
